/**
 * Unified UK National Curriculum Taxonomy Configuration
 * Used consistently across Frontend UI, Admin Dashboard, and Tutor Pages
 * 
 * This is the single source of truth for all curriculum-related data structures.
 * Backend API: /api/taxonomy provides the same data
 */

// =============================================================================
// KEY STAGES - Educational Levels
// =============================================================================
export const KEY_STAGES = [
  {
    id: 'ks1',
    name: 'Key Stage 1',
    shortName: 'KS1',
    years: ['year-1', 'year-2'],
    yearLabels: ['Year 1', 'Year 2'],
    ageRange: '5-7',
    description: 'Foundation years covering basic literacy and numeracy',
    subjects: ['maths', 'english', 'science'],
    order: 1
  },
  {
    id: 'ks2-lower',
    name: 'KS2 Lower',
    shortName: 'KS2-L',
    years: ['year-3', 'year-4'],
    yearLabels: ['Year 3', 'Year 4'],
    ageRange: '7-9',
    description: 'Building on foundations with more complex concepts',
    subjects: ['maths', 'english', 'science'],
    order: 2
  },
  {
    id: 'ks2-upper',
    name: 'KS2 Upper',
    shortName: 'KS2-U',
    years: ['year-5', 'year-6'],
    yearLabels: ['Year 5', 'Year 6'],
    ageRange: '9-11',
    description: 'Preparation for secondary school and 11+ exams',
    subjects: ['maths', 'english', 'science', 'history', 'geography'],
    order: 3
  },
  {
    id: '11-plus',
    name: '11+ Preparation',
    shortName: '11+',
    years: ['year-5', 'year-6'],
    yearLabels: ['Year 5', 'Year 6'],
    ageRange: '9-12',
    description: 'Selective school entrance exam preparation',
    subjects: ['maths', 'english', 'verbal-reasoning', 'non-verbal-reasoning'],
    order: 4
  },
  {
    id: 'ks3',
    name: 'Key Stage 3',
    shortName: 'KS3',
    years: ['year-7', 'year-8', 'year-9'],
    yearLabels: ['Year 7', 'Year 8', 'Year 9'],
    ageRange: '11-14',
    description: 'Secondary school foundation across all subjects',
    subjects: ['maths', 'english', 'science', 'history', 'geography', 'computing', 'art', 'pe', 'languages'],
    order: 5
  },
  {
    id: 'ks4',
    name: 'Key Stage 4 / GCSE',
    shortName: 'GCSE',
    years: ['year-10', 'year-11'],
    yearLabels: ['Year 10', 'Year 11'],
    ageRange: '14-16',
    description: 'GCSE preparation and examination years',
    subjects: ['maths', 'english', 'english-lang', 'english-lit', 'science', 'biology', 'chemistry', 'physics', 
              'combined-science', 'history', 'geography', 'computing', 'art', 'pe', 'languages', 
              'economics', 'business', 'psychology'],
    order: 6
  },
  {
    id: 'a-level',
    name: 'A-Level',
    shortName: 'A-Level',
    years: ['year-12', 'year-13'],
    yearLabels: ['Year 12', 'Year 13'],
    ageRange: '16-18',
    description: 'Advanced level preparation for university',
    subjects: ['maths', 'further-maths', 'english-lit', 'biology', 'chemistry', 'physics', 
              'history', 'geography', 'computing', 'economics', 'business', 'psychology', 'languages'],
    order: 7
  }
];

// Flat list for dropdowns (year-based selection)
export const YEAR_STAGES = [
  { value: 'year-1', label: 'Year 1 (KS1)', stage: 'ks1', age: '5-6' },
  { value: 'year-2', label: 'Year 2 (KS1)', stage: 'ks1', age: '6-7' },
  { value: 'year-3', label: 'Year 3 (KS2)', stage: 'ks2-lower', age: '7-8' },
  { value: 'year-4', label: 'Year 4 (KS2)', stage: 'ks2-lower', age: '8-9' },
  { value: 'year-5', label: 'Year 5 (KS2)', stage: 'ks2-upper', age: '9-10' },
  { value: 'year-6', label: 'Year 6 (KS2)', stage: 'ks2-upper', age: '10-11' },
  { value: '7plus', label: '7+ Entry', stage: '7-plus', age: '6-7' },
  { value: '11plus', label: '11+ Entry', stage: '11-plus', age: '10-11' },
  { value: 'year-7', label: 'Year 7 (KS3)', stage: 'ks3', age: '11-12' },
  { value: 'year-8', label: 'Year 8 (KS3)', stage: 'ks3', age: '12-13' },
  { value: 'year-9', label: 'Year 9 (KS3)', stage: 'ks3', age: '13-14' },
  { value: 'year-10', label: 'Year 10 (GCSE)', stage: 'ks4', age: '14-15' },
  { value: 'year-11', label: 'Year 11 (GCSE)', stage: 'ks4', age: '15-16' },
  { value: 'gcse', label: 'GCSE', stage: 'ks4', age: '14-16' },
  { value: 'year-12', label: 'Year 12 (A-Level)', stage: 'a-level', age: '16-17' },
  { value: 'year-13', label: 'Year 13 (A-Level)', stage: 'a-level', age: '17-18' },
  { value: 'a-levels', label: 'A-Levels', stage: 'a-level', age: '16-18' }
];

// =============================================================================
// SUBJECTS - Full Subject Catalog
// =============================================================================
export const SUBJECTS = [
  // Core Subjects
  { id: 'maths', name: 'Mathematics', shortName: 'Maths', category: 'core', icon: 'Calculator', color: '#4F46E5' },
  { id: 'english', name: 'English', shortName: 'Eng', category: 'core', icon: 'Book', color: '#7C3AED' },
  { id: 'english-lang', name: 'English Language', shortName: 'Eng Lang', category: 'core', icon: 'BookOpen', color: '#7C3AED' },
  { id: 'english-lit', name: 'English Literature', shortName: 'Eng Lit', category: 'core', icon: 'BookMarked', color: '#8B5CF6' },
  { id: 'science', name: 'Science', shortName: 'Sci', category: 'core', icon: 'FlaskConical', color: '#10B981' },
  
  // Sciences
  { id: 'biology', name: 'Biology', shortName: 'Bio', category: 'science', icon: 'Leaf', color: '#22C55E' },
  { id: 'chemistry', name: 'Chemistry', shortName: 'Chem', category: 'science', icon: 'Beaker', color: '#F59E0B' },
  { id: 'physics', name: 'Physics', shortName: 'Phys', category: 'science', icon: 'Atom', color: '#3B82F6' },
  { id: 'combined-science', name: 'Combined Science', shortName: 'Comb Sci', category: 'science', icon: 'FlaskConical', color: '#14B8A6' },
  
  // Reasoning (11+)
  { id: 'verbal-reasoning', name: 'Verbal Reasoning', shortName: 'VR', category: 'reasoning', icon: 'MessageSquare', color: '#EC4899' },
  { id: 'non-verbal-reasoning', name: 'Non-Verbal Reasoning', shortName: 'NVR', category: 'reasoning', icon: 'Shapes', color: '#F97316' },
  { id: 'reasoning', name: 'Reasoning', shortName: 'Reasoning', category: 'reasoning', icon: 'Brain', color: '#D946EF' },
  
  // Humanities
  { id: 'history', name: 'History', shortName: 'Hist', category: 'humanities', icon: 'Landmark', color: '#78716C' },
  { id: 'geography', name: 'Geography', shortName: 'Geog', category: 'humanities', icon: 'Globe', color: '#059669' },
  { id: 'economics', name: 'Economics', shortName: 'Econ', category: 'humanities', icon: 'TrendingUp', color: '#0891B2' },
  { id: 'business', name: 'Business Studies', shortName: 'Bus', category: 'humanities', icon: 'Briefcase', color: '#6366F1' },
  { id: 'psychology', name: 'Psychology', shortName: 'Psych', category: 'humanities', icon: 'Brain', color: '#DB2777' },
  
  // Technology
  { id: 'computing', name: 'Computer Science', shortName: 'Comp', category: 'technology', icon: 'Monitor', color: '#2563EB' },
  
  // Languages
  { id: 'languages', name: 'Languages', shortName: 'Lang', category: 'languages', icon: 'Languages', color: '#DC2626' },
  { id: 'french', name: 'French', shortName: 'French', category: 'languages', icon: 'Languages', color: '#1D4ED8' },
  { id: 'spanish', name: 'Spanish', shortName: 'Spanish', category: 'languages', icon: 'Languages', color: '#B91C1C' },
  { id: 'german', name: 'German', shortName: 'German', category: 'languages', icon: 'Languages', color: '#FBBF24' },
  
  // Creative/Physical
  { id: 'art', name: 'Art & Design', shortName: 'Art', category: 'creative', icon: 'Palette', color: '#F472B6' },
  { id: 'pe', name: 'Physical Education', shortName: 'PE', category: 'physical', icon: 'Dumbbell', color: '#EF4444' },
  
  // Advanced Maths
  { id: 'further-maths', name: 'Further Mathematics', shortName: 'FM', category: 'core', icon: 'Calculator', color: '#1E40AF' }
];

// Subject categories for filtering
export const SUBJECT_CATEGORIES = [
  { id: 'core', name: 'Core Subjects', subjects: ['maths', 'english', 'english-lang', 'english-lit', 'science', 'further-maths'] },
  { id: 'science', name: 'Sciences', subjects: ['biology', 'chemistry', 'physics', 'combined-science'] },
  { id: 'reasoning', name: 'Reasoning', subjects: ['verbal-reasoning', 'non-verbal-reasoning', 'reasoning'] },
  { id: 'humanities', name: 'Humanities', subjects: ['history', 'geography', 'economics', 'business', 'psychology'] },
  { id: 'technology', name: 'Technology', subjects: ['computing'] },
  { id: 'languages', name: 'Languages', subjects: ['languages', 'french', 'spanish', 'german'] },
  { id: 'creative', name: 'Creative', subjects: ['art'] },
  { id: 'physical', name: 'Physical', subjects: ['pe'] }
];

// =============================================================================
// DOMAINS - Topics within each subject by Key Stage
// =============================================================================
export const SUBJECT_DOMAINS = {
  // KS1 Domains
  'ks1': {
    'maths': [
      { id: 'number', name: 'Number & Place Value', weight: 25 },
      { id: 'addition_subtraction', name: 'Addition & Subtraction', weight: 25 },
      { id: 'multiplication_division', name: 'Multiplication & Division', weight: 15 },
      { id: 'fractions', name: 'Fractions', weight: 10 },
      { id: 'measurement', name: 'Measurement', weight: 15 },
      { id: 'geometry', name: 'Geometry', weight: 10 }
    ],
    'english': [
      { id: 'phonics', name: 'Phonics & Decoding', weight: 30 },
      { id: 'reading', name: 'Reading Comprehension', weight: 25 },
      { id: 'writing', name: 'Writing', weight: 20 },
      { id: 'spelling', name: 'Spelling', weight: 15 },
      { id: 'grammar', name: 'Grammar', weight: 10 }
    ],
    'science': [
      { id: 'plants_animals', name: 'Plants & Animals', weight: 30 },
      { id: 'materials', name: 'Everyday Materials', weight: 25 },
      { id: 'seasonal_changes', name: 'Seasonal Changes', weight: 20 },
      { id: 'human_body', name: 'Human Body', weight: 25 }
    ]
  },
  // KS2-Lower Domains
  'ks2-lower': {
    'maths': [
      { id: 'number', name: 'Number & Place Value', weight: 20 },
      { id: 'addition_subtraction', name: 'Addition & Subtraction', weight: 20 },
      { id: 'multiplication_division', name: 'Multiplication & Division', weight: 20 },
      { id: 'fractions', name: 'Fractions & Decimals', weight: 15 },
      { id: 'measurement', name: 'Measurement', weight: 15 },
      { id: 'geometry', name: 'Geometry', weight: 10 }
    ],
    'english': [
      { id: 'reading', name: 'Reading Comprehension', weight: 30 },
      { id: 'vocabulary', name: 'Vocabulary', weight: 20 },
      { id: 'writing', name: 'Writing Composition', weight: 20 },
      { id: 'grammar', name: 'Grammar & Punctuation', weight: 15 },
      { id: 'spelling', name: 'Spelling', weight: 15 }
    ],
    'science': [
      { id: 'living_things', name: 'Living Things', weight: 30 },
      { id: 'materials', name: 'States of Matter', weight: 25 },
      { id: 'forces', name: 'Forces & Magnets', weight: 20 },
      { id: 'sound_light', name: 'Sound & Light', weight: 25 }
    ]
  },
  // KS3 Domains
  'ks3': {
    'maths': [
      { id: 'algebra', name: 'Algebra', weight: 20 },
      { id: 'fractions', name: 'Fractions & Decimals', weight: 15 },
      { id: 'percentages', name: 'Percentages', weight: 10 },
      { id: 'ratio', name: 'Ratio & Proportion', weight: 10 },
      { id: 'geometry', name: 'Geometry', weight: 20 },
      { id: 'statistics', name: 'Statistics', weight: 15 },
      { id: 'probability', name: 'Probability', weight: 10 }
    ],
    'english': [
      { id: 'comprehension', name: 'Reading Comprehension', weight: 20 },
      { id: 'vocabulary', name: 'Vocabulary', weight: 15 },
      { id: 'grammar', name: 'Grammar', weight: 15 },
      { id: 'literary_devices', name: 'Literary Devices', weight: 20 },
      { id: 'writing', name: 'Writing Skills', weight: 30 }
    ],
    'science': [
      { id: 'biology', name: 'Biology', weight: 33 },
      { id: 'chemistry', name: 'Chemistry', weight: 34 },
      { id: 'physics', name: 'Physics', weight: 33 }
    ],
    'history': [
      { id: 'medieval', name: 'Medieval Britain', weight: 30 },
      { id: 'tudor', name: 'Tudor England', weight: 30 },
      { id: 'industrial', name: 'Industrial Revolution', weight: 20 },
      { id: 'empire', name: 'British Empire', weight: 20 }
    ],
    'geography': [
      { id: 'physical', name: 'Physical Geography', weight: 40 },
      { id: 'human', name: 'Human Geography', weight: 40 },
      { id: 'skills', name: 'Geographical Skills', weight: 20 }
    ],
    'computing': [
      { id: 'algorithms', name: 'Algorithms', weight: 25 },
      { id: 'programming', name: 'Programming', weight: 25 },
      { id: 'data', name: 'Data Representation', weight: 25 },
      { id: 'systems', name: 'Computer Systems', weight: 25 }
    ],
    'art': [
      { id: 'drawing', name: 'Drawing & Observational Skills', weight: 25 },
      { id: 'painting', name: 'Painting', weight: 25 },
      { id: 'sculpture', name: '3D Work & Sculpture', weight: 25 },
      { id: 'artists', name: 'Art History & Artists', weight: 25 }
    ],
    'pe': [
      { id: 'games', name: 'Games & Team Sports', weight: 30 },
      { id: 'gymnastics', name: 'Gymnastics', weight: 20 },
      { id: 'athletics', name: 'Athletics', weight: 20 },
      { id: 'fitness', name: 'Health & Fitness', weight: 30 }
    ],
    'languages': [
      { id: 'grammar', name: 'Grammar', weight: 25 },
      { id: 'vocabulary', name: 'Vocabulary', weight: 25 },
      { id: 'reading', name: 'Reading', weight: 20 },
      { id: 'writing', name: 'Writing', weight: 15 },
      { id: 'speaking', name: 'Speaking & Listening', weight: 15 }
    ]
  },
  // KS4 / GCSE Domains
  'ks4': {
    'maths': [
      { id: 'algebra', name: 'Algebra', weight: 25 },
      { id: 'trigonometry', name: 'Trigonometry', weight: 15 },
      { id: 'geometry', name: 'Geometry & Measures', weight: 20 },
      { id: 'probability', name: 'Probability', weight: 10 },
      { id: 'statistics', name: 'Statistics', weight: 10 },
      { id: 'surds', name: 'Surds & Indices', weight: 10 },
      { id: 'graphs', name: 'Graphs & Functions', weight: 10 }
    ],
    'english': [
      { id: 'inference', name: 'Inference & Analysis', weight: 20 },
      { id: 'analysis', name: 'Text Analysis', weight: 15 },
      { id: 'literature', name: 'Literature Study', weight: 20 },
      { id: 'grammar', name: 'Advanced Grammar', weight: 10 },
      { id: 'writing', name: 'Writing Skills', weight: 20 },
      { id: 'poetry', name: 'Poetry Analysis', weight: 15 }
    ],
    'science': [
      { id: 'biology', name: 'Biology', weight: 33 },
      { id: 'chemistry', name: 'Chemistry', weight: 34 },
      { id: 'physics', name: 'Physics', weight: 33 }
    ],
    'history': [
      { id: 'weimar_nazi', name: 'Weimar & Nazi Germany', weight: 25 },
      { id: 'cold_war', name: 'Cold War', weight: 25 },
      { id: 'british_history', name: 'British History', weight: 25 },
      { id: 'international', name: 'International Relations', weight: 25 }
    ],
    'geography': [
      { id: 'physical', name: 'Physical Geography', weight: 35 },
      { id: 'human', name: 'Human Geography', weight: 35 },
      { id: 'fieldwork', name: 'Fieldwork & Skills', weight: 15 },
      { id: 'issues', name: 'Geographical Issues', weight: 15 }
    ],
    'computing': [
      { id: 'algorithms', name: 'Algorithms', weight: 20 },
      { id: 'programming', name: 'Programming', weight: 25 },
      { id: 'data', name: 'Data Representation', weight: 20 },
      { id: 'systems', name: 'Computer Systems', weight: 15 },
      { id: 'networks', name: 'Networks & Security', weight: 10 },
      { id: 'ethics', name: 'Ethics & Impacts', weight: 10 }
    ],
    'art': [
      { id: 'portfolio', name: 'Portfolio Development', weight: 40 },
      { id: 'externally_set', name: 'Externally Set Assignment', weight: 30 },
      { id: 'artists', name: 'Artist Research', weight: 30 }
    ],
    'pe': [
      { id: 'practical', name: 'Practical Performance', weight: 40 },
      { id: 'anatomy', name: 'Anatomy & Physiology', weight: 20 },
      { id: 'training', name: 'Training & Fitness', weight: 20 },
      { id: 'psychology', name: 'Sport Psychology', weight: 10 },
      { id: 'socio_cultural', name: 'Socio-Cultural Influences', weight: 10 }
    ],
    'economics': [
      { id: 'micro', name: 'Microeconomics', weight: 50 },
      { id: 'macro', name: 'Macroeconomics', weight: 50 }
    ],
    'business': [
      { id: 'enterprise', name: 'Enterprise & Marketing', weight: 25 },
      { id: 'finance', name: 'Finance', weight: 25 },
      { id: 'people', name: 'Human Resources', weight: 25 },
      { id: 'operations', name: 'Operations', weight: 25 }
    ],
    'psychology': [
      { id: 'memory', name: 'Memory', weight: 20 },
      { id: 'perception', name: 'Perception', weight: 20 },
      { id: 'development', name: 'Development', weight: 20 },
      { id: 'research_methods', name: 'Research Methods', weight: 20 },
      { id: 'social', name: 'Social Influence', weight: 20 }
    ],
    'languages': [
      { id: 'listening', name: 'Listening', weight: 25 },
      { id: 'speaking', name: 'Speaking', weight: 25 },
      { id: 'reading', name: 'Reading', weight: 25 },
      { id: 'writing', name: 'Writing', weight: 25 }
    ]
  }
};

// =============================================================================
// DIFFICULTY LEVELS
// =============================================================================
export const DIFFICULTY_LEVELS = [
  { id: 1, name: 'Foundation', label: 'Easy', color: '#22C55E' },
  { id: 2, name: 'Developing', label: 'Easy-Medium', color: '#84CC16' },
  { id: 3, name: 'Secure', label: 'Medium', color: '#EAB308' },
  { id: 4, name: 'Extending', label: 'Medium-Hard', color: '#F97316' },
  { id: 5, name: 'Mastery', label: 'Hard', color: '#EF4444' }
];

// Legacy format for backwards compatibility
export const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', level: 1 },
  { value: 'medium', label: 'Medium', level: 3 },
  { value: 'hard', label: 'Hard', level: 5 }
];

// =============================================================================
// QUESTION TYPES
// =============================================================================
export const QUESTION_TYPES = [
  { id: 'mcq', name: 'Multiple Choice', short: 'MCQ' },
  { id: 'short_answer', name: 'Short Answer', short: 'SA' },
  { id: 'long_answer', name: 'Long Answer', short: 'LA' },
  { id: 'problem_solving', name: 'Problem Solving', short: 'PS' },
  { id: 'practical', name: 'Practical', short: 'PR' },
  { id: 'data_interpretation', name: 'Data Interpretation', short: 'DI' }
];

// =============================================================================
// COGNITIVE LEVELS (Bloom's Taxonomy)
// =============================================================================
export const COGNITIVE_LEVELS = [
  { id: 'recall', name: 'Recall/Remember', level: 1 },
  { id: 'understand', name: 'Understand', level: 2 },
  { id: 'apply', name: 'Apply', level: 3 },
  { id: 'analyse', name: 'Analyse', level: 4 },
  { id: 'evaluate', name: 'Evaluate', level: 5 },
  { id: 'create', name: 'Create', level: 6 }
];

// =============================================================================
// MASTERY LEVELS
// =============================================================================
export const MASTERY_LEVELS = [
  { id: 'beginner', name: 'Beginner', min: 0, max: 25, color: '#EF4444' },
  { id: 'developing', name: 'Developing', min: 26, max: 50, color: '#F97316' },
  { id: 'secure', name: 'Secure', min: 51, max: 75, color: '#EAB308' },
  { id: 'advanced', name: 'Advanced', min: 76, max: 90, color: '#22C55E' },
  { id: 'mastery', name: 'Mastery', min: 91, max: 100, color: '#10B981' }
];

// =============================================================================
// EXAM BOARDS
// =============================================================================
export const EXAM_BOARDS = [
  { id: 'aqa', name: 'AQA', fullName: 'Assessment and Qualifications Alliance' },
  { id: 'edexcel', name: 'Edexcel', fullName: 'Pearson Edexcel' },
  { id: 'ocr', name: 'OCR', fullName: 'Oxford, Cambridge and RSA' },
  { id: 'wjec', name: 'WJEC', fullName: 'Welsh Joint Education Committee' },
  { id: 'cie', name: 'CAIE', fullName: 'Cambridge Assessment International Education' }
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================
export const getStageById = (stageId) => KEY_STAGES.find(s => s.id === stageId);
export const getSubjectById = (subjectId) => SUBJECTS.find(s => s.id === subjectId);
export const getSubjectLabel = (subjectId) => SUBJECTS.find(s => s.id === subjectId)?.name || subjectId;
export const getStageLabel = (stageId) => KEY_STAGES.find(s => s.id === stageId)?.name || stageId;
export const getYearLabel = (yearId) => YEAR_STAGES.find(y => y.value === yearId)?.label || yearId;

export const getSubjectsForStage = (stageId) => {
  const stage = getStageById(stageId);
  if (!stage) return [];
  return SUBJECTS.filter(s => stage.subjects.includes(s.id));
};

export const getDomainsForStageSubject = (stageId, subjectId) => {
  return SUBJECT_DOMAINS[stageId]?.[subjectId] || [];
};

export const getDifficultyLabel = (level) => {
  return DIFFICULTY_LEVELS.find(d => d.id === level)?.label || 'Unknown';
};

export const getDifficultyColor = (level) => {
  return DIFFICULTY_LEVELS.find(d => d.id === level)?.color || '#6B7280';
};

export const getMasteryLevel = (score) => {
  return MASTERY_LEVELS.find(m => score >= m.min && score <= m.max) || MASTERY_LEVELS[0];
};

// Get stage from year group
export const getStageFromYear = (yearValue) => {
  if (!yearValue) return null;
  
  // Normalize the input - handle both "Year 13" and "year-13" formats
  const normalizedYear = yearValue.toLowerCase().replace(/\s+/g, '-').replace('year-', 'year-');
  
  // Direct match
  const year = YEAR_STAGES.find(y => y.value === normalizedYear);
  if (year) return year.stage;
  
  // Check for partial matches (e.g., "Year 13" should match "year-13")
  const numMatch = yearValue.match(/(\d+)/);
  if (numMatch) {
    const yearNum = numMatch[1];
    const matchedYear = YEAR_STAGES.find(y => y.value === `year-${yearNum}`);
    if (matchedYear) return matchedYear.stage;
  }
  
  // Check for stage keywords directly
  if (yearValue.toLowerCase().includes('a-level') || yearValue.toLowerCase().includes('alevel')) {
    return 'a-level';
  }
  if (yearValue.toLowerCase().includes('gcse')) {
    return 'ks4';
  }
  
  return null;
};

// =============================================================================
// EXPORT DEFAULT CONFIG
// =============================================================================
const taxonomyConfig = {
  KEY_STAGES,
  YEAR_STAGES,
  SUBJECTS,
  SUBJECT_CATEGORIES,
  SUBJECT_DOMAINS,
  DIFFICULTY_LEVELS,
  DIFFICULTIES,
  QUESTION_TYPES,
  COGNITIVE_LEVELS,
  MASTERY_LEVELS,
  EXAM_BOARDS,
  // Helper functions
  getStageById,
  getSubjectById,
  getSubjectLabel,
  getStageLabel,
  getYearLabel,
  getSubjectsForStage,
  getDomainsForStageSubject,
  getDifficultyLabel,
  getDifficultyColor,
  getMasteryLevel,
  getStageFromYear
};

export default taxonomyConfig;
